import React, { useCallback, useContext, useEffect, useState } from "react";
import { DrawModeChangeEvent } from "@mapbox/mapbox-gl-draw";
import { Add, CenterFocusStrong, LocationSearching, MyLocation, Remove } from "@mui/icons-material";

import analytics from "fond/analytics";
import { StreetViewIcon } from "fond/svg_icons";

import { EditMode } from "../types";
import { SvgIcon } from "../widgets";

import MapButton from "./MapButton";
import { MapContext } from "./MapProvider";

import { Container } from "./BaseMapButtons.styles";

interface BaseMapButtonsProps {
  editMode: EditMode;
  onClickMeasure?: () => void;
  onClickStreetView?: () => void;
  onClickZoomToDesign?: () => void;
  onClickZoomIn?: () => void;
  onClickZoomOut?: () => void;
  currentLocationButton?: boolean;
  streetViewButton?: boolean;
}

const BaseMapButtons: React.FC<BaseMapButtonsProps> = ({
  editMode,
  onClickMeasure,
  onClickStreetView,
  onClickZoomToDesign,
  onClickZoomIn,
  onClickZoomOut,
  currentLocationButton,
  streetViewButton = false,
}) => {
  const { userPosition, startPositionWatch, stopPositionWatch } = useContext(MapContext);
  const { map } = useContext(MapContext);
  const [mode, setMode] = useState<string | null>(null);

  /**
   * We monitor the draw mode to highlight the street view button when in street view mode.
   */
  const handleModeChange = useCallback(
    (event: DrawModeChangeEvent) => {
      setMode(event.mode);
    },
    [setMode]
  );

  useEffect(() => {
    map?.on("draw.modechange", handleModeChange);
    return () => {
      map?.off("draw.modechange", handleModeChange);
    };
  }, [map, handleModeChange]);

  const onClickCurrentLocationHandler = () => {
    if (!userPosition) {
      startPositionWatch();
    } else {
      stopPositionWatch();
    }
  };

  const onClickMeasureHandler = () => {
    analytics.track("Clicked measure distance button");
    onClickMeasure!();
  };

  const onClickZoomToDesignHandler = () => {
    analytics.track("Clicked zoom to design button");
    onClickZoomToDesign!();
  };

  const onClickZoomInHandler = () => {
    analytics.track("Clicked zoom in button");
    onClickZoomIn!();
  };

  const onClickZoomOutHandler = () => {
    analytics.track("Clicked zoom out button");
    onClickZoomOut!();
  };

  const onClickStreetViewHandler = () => {
    analytics.track("map.controls.streetview.clicked");
    onClickStreetView!();
  };

  return (
    <Container>
      {streetViewButton && (
        <MapButton
          id="streetView"
          title="Open street view"
          dataT="map-street-view"
          onClick={onClickStreetViewHandler}
          active={mode === "street_view"}
        >
          <StreetViewIcon width={24} height={24} />
        </MapButton>
      )}
      {(currentLocationButton ?? true) && (
        <MapButton
          id="currentLocation"
          title={`${!userPosition ? "Show" : "Hide"} Current Location`}
          dataT="map-current-location"
          onClick={onClickCurrentLocationHandler}
          active={userPosition !== null}
        >
          {userPosition ? <MyLocation /> : <LocationSearching />}
        </MapButton>
      )}
      {onClickMeasure !== undefined && (
        <MapButton
          id="measure"
          title={editMode === "measure" ? "Stop measuring" : "Measure a distance"}
          dataT="map-button-measure"
          onClick={onClickMeasureHandler}
          active={editMode === "measure"}
          enabled={editMode === "none" || editMode === "measure"}
        >
          <SvgIcon icon="ruler" />
        </MapButton>
      )}
      {onClickZoomToDesign !== undefined && (
        <MapButton id="zoomTo" title="Zoom to design" dataT="map-button-zoom-to" onClick={onClickZoomToDesignHandler}>
          <CenterFocusStrong />
        </MapButton>
      )}
      {onClickZoomIn !== undefined && (
        <MapButton id="zoomIn" title="Zoom in" dataT="map-button-zoom-in" onClick={onClickZoomInHandler}>
          <Add />
        </MapButton>
      )}
      {onClickZoomOut !== undefined && (
        <MapButton id="zoomOut" title="Zoom out" dataT="map-button-zoom-out" onClick={onClickZoomOutHandler}>
          <Remove />
        </MapButton>
      )}
    </Container>
  );
};

export default BaseMapButtons;
